import { Injectable } from '@angular/core';
import { IContentLocation, IReaderViewAnnotation, IReaderViewAnnotationOptions, ISelectionChangedEngineEvent } from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-base';
import { IMarkerColor, IMarkerContent } from 'src/app/core/interfaces';
import { DocumentManagerService } from '../document/document-manager.service';
import { BaseEpubAnnotationService } from './base-epub-annotation.service';
import { ILocator } from '@colibrio/colibrio-reader-framework/colibrio-core-locator';
import { CommandInvokerService } from '../command/command-invoker.service';
import { GoogleAnalyticsActions, GoogleAnalyticsService } from '../google-analytics.service';

interface IEpubHighlightCustomData {
    color: IMarkerColor;
    location: IContentLocation;
}

@Injectable({
    providedIn: 'root'
})
export class EpubHighlightService extends BaseEpubAnnotationService<IEpubHighlightCustomData> {
    private activeColor?: IMarkerColor;

    public readonly LAYER_NAME = 'highlights';

    constructor(
        documentManagerService: DocumentManagerService, 
        commandInvoker: CommandInvokerService,
        private readonly googleAnalyticsService: GoogleAnalyticsService
    ) {
        super(documentManagerService, commandInvoker);
    }

    protected createAnnotation(locator: ILocator, data: IEpubHighlightCustomData) {
        this.readerPublication?.fetchContentLocation(locator).then(location => {
            this.createAnnotationHighlight(location, data.color);
        })
    }

    public setColor(color: IMarkerColor) {
        this.activeColor = color;
    }

    public createHighlight(event: ISelectionChangedEngineEvent) {
        this.googleAnalyticsService.trackAction(GoogleAnalyticsActions.HIGHLIGHT_ADDED, this.activeColor?.color ?? 'unknown');
        if (!this.isValidEvent(event)) return;

        this.createAnnotationHighlight(event.contentLocation!, this.activeColor);
    }

    public eraseHighlight(event: ISelectionChangedEngineEvent) {
        this.googleAnalyticsService.trackAction(GoogleAnalyticsActions.HIGHLIGHT_ADDED, 'Eraser');
        if (!this.isValidEvent(event)) return;
        this.annotationLayer?.getAnnotations().filter(highlight => {
            const higlightData = highlight?.getCustomData();
            if (!higlightData) return false;
            return event.contentLocation!.intersects(higlightData.location);
        }).forEach(highlight => this.splitOverlappingHighlight(highlight, event.contentLocation!));
    }

    public async getMarkers(): Promise<IMarkerContent[]> {
        if (!this.annotationLayer) return [];

        const results: IMarkerContent[] = [];
        for (const h of this.annotationLayer!.getAnnotations()) {
            const highlightData = h.getCustomData() as IEpubHighlightCustomData;
            const text = (await highlightData.location.fetchContentResolver()).getTextContent();
            results.push({ text: text, color: highlightData.color.hex.toLowerCase() });
        }
        return results;
    }

    private isValidEvent(event: ISelectionChangedEngineEvent): boolean {
        return !!(event.contentLocation && event.selectionText && this.annotationLayer);
    }

    private splitOverlappingHighlight(highlight: IReaderViewAnnotation<IEpubHighlightCustomData>, splitter: IContentLocation) {
        if (!splitter || !highlight.getCustomData()) return;

        const location = highlight.getCustomData()!.location;
        const newHighlightsLocations: IContentLocation[] = [];
        const startOfSplitter = splitter.collapseToStart();
        const endOfSplitter = splitter.collapseToEnd();
        const startOfHighlight = location.collapseToStart();
        const endOfHighlight = location.collapseToEnd();

        if (startOfSplitter.isBefore(location) || location.contains(splitter)) {
            if (!endOfSplitter.isAfter(endOfHighlight)) {
                newHighlightsLocations.push(endOfSplitter.createRangeTo(endOfHighlight));
            }
        }

        if (endOfSplitter.isAfter(location) || location.contains(splitter)) {
            if (!startOfSplitter.isBefore(startOfHighlight)) {
                newHighlightsLocations.push(startOfHighlight.createRangeTo(startOfSplitter));
            }
        }

        this.annotationLayer?.destroyAnnotation(highlight);
        newHighlightsLocations.forEach(location => {
            this.createAnnotationHighlight(location, highlight.getCustomData()!.color);
        });
    }

    private createAnnotationHighlight(location: IContentLocation, color?: IMarkerColor) {
        const highlight = this.annotationLayer?.createAnnotation(location);
        if (!highlight) return;

        highlight.setCustomData({
            color: color,
            location: location
        } as IEpubHighlightCustomData);

        if (color) {
            highlight.setOptions(this.getHighlightColorStyle(color));
        }

        this.annotationLayer?.refreshAnnotations();
        this.emitDocChanged(this.docId!);
    }

    private getHighlightColorStyle(color: IMarkerColor): IReaderViewAnnotationOptions {
        return {
            rangeStyle: {
                'background-color': color.actualColor,
            }
        }
    }

}